import {
  DatabaseAuto, IdataArrayResponse, IfilterProps,
  IunreliableBuyer
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Represents an unreliable buyer entry for a specific order.
 * Extends DatabaseAuto to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UnreliableBuyer} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const unreliableBuyer = await UnreliableBuyer.getAll();
 *
 * const unreliableBuyer = await UnreliableBuyer.filterAll({
 *
 * });
 *
 * const unreliableBuyer = await UnreliableBuyer.bulkDelete({
 *
 * });
 *
 * const unreliableBuyer = await UnreliableBuyer.getOne('resourceId');
 *
 * const unreliableBuyer = await UnreliableBuyer.remove();
 *
 * ```
 */
export class UnreliableBuyer
  extends DatabaseAuto {
  /** Base URL for the unreliable buyer API endpoint */
  private static readonly baseUrl = '/un-reliable-buyer';

  /** Flagged buyers associated with the unreliable buyer */
  readonly flaggedBuyers: IunreliableBuyer['flaggedBuyers'];

  /**
   * Constructs an instance of `unreliableBuyer`.
   *
   * @param {IunreliableBuyer} data - The unreliableBuyer data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: IunreliableBuyer) {
    super(data);

    this.flaggedBuyers = data.flaggedBuyers;
  }

  /**
   * Retrieves all unreliableBuyers with pagination.
   *
   * @param {number} offset - The starting point for the retrieval.
   * @param {number} limit - The number of records to retrieve.
   * @returns {Promise<{ count: number; dataArr: UnreliableBuyer[] }>}
   * A promise resolving to an object containing count and data array.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IunreliableBuyer>>(`${UnreliableBuyer.baseUrl}?${query}`);
    const unreliableBuyers = await lastValueFrom(observer$);

    return {
      count: unreliableBuyers.count,
      dataArr: unreliableBuyers.data.map((val) => new UnreliableBuyer(val))
    };
  }

  /**
   * Filters and retrieves unreliable buyers based on the provided criteria.
   *
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{count: number, dataArr: UnreliableBuyer[]}>} Object containing:
   * - count: Total number of matching unreliable buyers
   * - dataArr: Array of UnreliableBuyer instances
   * @throws {Error} When the API request fails or filter is invalid
   *
   * @example
   * const result = await UnreliableBuyer.filterAll({
   *   where: { 'flaggedBuyers.reason': 'Payment issues' },
   *   sort: { 'flaggedBuyers.flaggedAt': -1 }
   * });
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IunreliableBuyer>>(
        `${UnreliableBuyer.baseUrl}/filter`,
        filter
      );
    const unreliableBuyers = await lastValueFrom(observer$);

    return {
      count: unreliableBuyers.count,
      dataArr: unreliableBuyers.data.map((val) => new UnreliableBuyer(val))
    };
  }

  /**
   * Retrieves a single unreliable buyer by their unique identifier.
   *
   * @param {string} resourceId - The unique identifier of the unreliable buyer to retrieve
   * @returns {Promise<UnreliableBuyer>} The requested UnreliableBuyer instance
   * @throws {Error} When the buyer is not found or API request fails
   *
   * @example
   * try {
   *   const buyer = await UnreliableBuyer.getOne('buyer123');
   *   console.log(buyer.flaggedBuyers);
   * } catch (error) {
   *   console.error('Failed to fetch unreliable buyer:', error);
   * }
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IunreliableBuyer>(`${UnreliableBuyer.baseUrl}/${resourceId}`);
    const unreliableBuyer = await lastValueFrom(observer$);

    return new UnreliableBuyer(unreliableBuyer);
  }

  /**
   * Modifies an unreliable buyer's status by adding or removing them from watchlists.
   *
   * @param {string} buyerId - The ID of the buyer to modify
   * @param {'red' | 'warn'} listType - The type of list to modify:
   *   - 'red': For serious violations (immediate restrictions)
   *   - 'warn': For warning-level issues
   * @param {'add' | 'remove'} action - The action to perform
   * @param {number} [warnCount] - The number of warnings to add (only used when action is 'add' and listType is 'warn')
   * @returns {Promise<UnreliableBuyer>} The updated UnreliableBuyer instance
   * @throws {Error} When the modification fails or validation fails
   *
   * @example
   * // Add a warning to a buyer
   * const warnedBuyer = await UnreliableBuyer.modify('user123', 'warn', 'add', 1);
   *
   * // Remove a buyer from the red list
   * const updatedBuyer = await UnreliableBuyer.modify('user123', 'red', 'remove');
   */
  static async modify(
    buyerId: string,
    listType: 'red' | 'warn',
    action: 'add' | 'remove',
    warnCount?: number
  ) {
    const observer$ = CommerceClient.ehttp
      .makePut<IunreliableBuyer>(
        `${UnreliableBuyer.baseUrl}`,
        { buyerId, listType, action, warnCount }
      );

    return new UnreliableBuyer(await lastValueFrom(observer$));
  }
}
